import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Campaign as CampaignIcon,
  CalendarToday as CalendarIcon,
  Close,
} from '@mui/icons-material';
import { GuestSpeaker, TableColumn } from '../types';
import apiService from '../services/api';
import SpeakerEditDialog from '../components/modals/SpeakerEditDialog';
import SpeakerViewDialog from '../components/modals/SpeakerViewDialog';
import PermissionGuard from '../components/PermissionGuard';
import { Permissions, hasPermission } from '../utils/permissions';
import { useAuth } from '../context/useAuth';
import DataTable from '../components/dataTable';

const GuestSpeakers: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [speakers, setSpeakers] = useState<GuestSpeaker[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filters
  const [filters, setFilters] = useState<{
    searchTerm?: string;
    isActive?: boolean;
    isKeynoteSpeaker?: boolean;
    isFeaturedSpeaker?: boolean;
    page: number;
    limit: number;
  }>({
    page: 1,
    limit: 25,
    searchTerm: '',
    isActive: undefined,
    isKeynoteSpeaker: undefined,
    isFeaturedSpeaker: undefined,
  });

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingSpeaker, setEditingSpeaker] = useState<GuestSpeaker | null>(
    null
  );
  const [viewingSpeaker, setViewingSpeaker] = useState<GuestSpeaker | null>(
    null
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [speakerToDelete, setSpeakerToDelete] = useState<GuestSpeaker | null>(
    null
  );
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [selectedSpeaker, setSelectedSpeaker] = useState<GuestSpeaker | null>(
    null
  );

  const fetchSpeakers = async () => {
    setLoading(true);
    try {
      const response = await apiService.getGuestSpeakers(filters);
      setSpeakers(response.data || []);
      setTotal(response.total || 0);
      // console.log('All speakers', response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch speakers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSpeakers();
  }, [filters]);

  const handleViewSpeaker = (speaker: GuestSpeaker) => {
    setViewingSpeaker(speaker);
    setViewDialogOpen(true);
    setActionMenuAnchor(null);
  };

  const handleEditSpeaker = (speaker: GuestSpeaker) => {
    setEditingSpeaker(speaker);
    setEditDialogOpen(true);
    setActionMenuAnchor(null);
  };

  const handleDeleteSpeaker = (speaker: GuestSpeaker) => {
    setSpeakerToDelete(speaker);
    setDeleteDialogOpen(true);
    setActionMenuAnchor(null);
  };

  const confirmDelete = async () => {
    if (!speakerToDelete) return;

    try {
      await apiService.deleteGuestSpeaker(speakerToDelete.id);
      setDeleteDialogOpen(false);
      setSpeakerToDelete(null);
      fetchSpeakers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete speaker');
    }
  };

  const handleToggleStatus = async (speaker: GuestSpeaker) => {
    try {
      await apiService.toggleGuestSpeakerStatus(speaker.id);
      fetchSpeakers();
      setActionMenuAnchor(null);
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Failed to toggle speaker status'
      );
    }
  };

  const handleToggleFeatured = async (speaker: GuestSpeaker) => {
    try {
      await apiService.toggleGuestSpeakerFeatured(speaker.id);
      fetchSpeakers();
      setActionMenuAnchor(null);
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Failed to toggle featured status'
      );
    }
  };

  const handleToggleKeynote = async (speaker: GuestSpeaker) => {
    try {
      await apiService.toggleGuestSpeakerKeynote(speaker.id);
      fetchSpeakers();
      setActionMenuAnchor(null);
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Failed to toggle keynote status'
      );
    }
  };

  const handleSpeakerSaved = () => {
    setEditDialogOpen(false);
    setEditingSpeaker(null);
    fetchSpeakers();
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1, // Reset to first page when filtering
    }));
    setPage(0);
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setFilters(prev => ({ ...prev, page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setFilters(prev => ({ ...prev, limit: newRowsPerPage, page: 1 }));
  };

  const openActionMenu = (
    event: React.MouseEvent<HTMLElement>,
    speaker: GuestSpeaker
  ) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedSpeaker(speaker);
  };

  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setSelectedSpeaker(null);
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive
      ? { bg: '#dcfce7', color: '#16a34a' }
      : { bg: '#fee2e2', color: '#dc2626' };
  };

  // if (loading && speakers.length === 0) {
  //   return (
  //     <Box
  //       sx={{
  //         display: 'flex',
  //         justifyContent: 'center',
  //         alignItems: 'center',
  //         height: '50vh',
  //       }}
  //     >
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  const columns: TableColumn<GuestSpeaker>[] = [
    {
      label: 'Name',
      keys: ['firstName', 'lastName', 'designation', 'profileImageUrl'],
      renderGroup: values => {
        const [firstName, lastName, designation, profileImageUrl] = values as [
          string,
          string,
          string,
          string,
        ];
        const getInitials = (firstName: string, lastName: string) => {
          return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
        };
        return (
          <Box
            sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
          >
            <Avatar
              src={profileImageUrl}
              sx={{
                width: '40px',
                height: '40px',
                fontSize: '0.875rem',
                fontWeight: 500,
                backgroundColor: '#7c3aed',
              }}
            >
              {getInitials(firstName, lastName)}
            </Avatar>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                marginLeft: '0.75rem',
              }}
            >
              {firstName} {lastName}
              <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                {designation}
              </span>
            </Box>
          </Box>
        );
      },
    },
    {
      label: 'Organization',
      key: 'organization',
      renderGroup: values => {
        const [organization] = values as [string];
        return <Box>{organization ? organization : 'N/A'}</Box>;
      },
    },
    {
      label: 'Contact',
      keys: ['email', 'phone'],
      renderGroup: values => {
        const [email, phone] = values as [string, string];
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: '0.875rem' }}>{email}</span>
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              {phone}
            </span>
          </Box>
        );
      },
    },
    {
      label: 'Type',
      keys: ['isFeaturedSpeaker', 'isKeynoteSpeaker'],
      renderGroup: values => {
        const [isFeaturedSpeaker, isKeynoteSpeaker] = values as [
          boolean,
          boolean,
        ];
        return (
          <>
            {isFeaturedSpeaker && (
              <Chip
                icon={
                  <StarIcon
                    sx={{ fontSize: 16, color: '#1d4ed8 !important' }}
                  />
                }
                label="Featured"
                sx={{
                  bgcolor: '#dbeafe',
                  color: '#1d4ed8',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  height: 24,
                }}
              />
            )}
            {isKeynoteSpeaker && (
              <Chip
                icon={
                  <CampaignIcon
                    sx={{ fontSize: 16, color: '#d97706 !important' }}
                  />
                }
                label="Keynote"
                sx={{
                  bgcolor: '#fef3c7',
                  color: '#d97706',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  height: 24,
                }}
              />
            )}
            {!isFeaturedSpeaker && !isKeynoteSpeaker && (
              <Chip
                label="Regular"
                sx={{
                  bgcolor: '#DBDBDB',
                  color: '#3C3E42',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  height: 24,
                }}
              />
            )}
          </>
        );
      },
    },
    { label: 'Status', key: 'isActive', chip: 'status' },
  ];

  return (
    <Box sx={{ p: 3, bgcolor: '#f9fafb', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontSize: '1.875rem',
            fontWeight: 700,
            color: '#111827',
          }}
        >
          Guest Speakers Management
        </Typography>
        <PermissionGuard permission={Permissions.USER_CREATE}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              bgcolor: '#7c3aed',
              '&:hover': { bgcolor: '#6d28d9' },
              textTransform: 'none',
              fontWeight: 500,
              px: 2,
              py: 1,
            }}
            onClick={() => setEditDialogOpen(true)}
          >
            Add New Speaker
          </Button>
        </PermissionGuard>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Main Content Card */}
      <Card
        sx={{
          boxShadow:
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          borderRadius: 2,
        }}
      >
        {/* Filters Section */}
        <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}
            >
              <TextField
                placeholder="Search speakers..."
                value={filters.searchTerm}
                onChange={e => handleFilterChange('searchTerm', e.target.value)}
                size="small"
                sx={{ minWidth: 250 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={
                    filters.isActive === undefined
                      ? ''
                      : filters.isActive.toString()
                  }
                  onChange={e =>
                    handleFilterChange(
                      'isActive',
                      e.target.value === ''
                        ? undefined
                        : e.target.value === 'true'
                    )
                  }
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Speaker Type</InputLabel>
                <Select
                  value={
                    filters.isKeynoteSpeaker === true
                      ? 'keynote'
                      : filters.isFeaturedSpeaker === true
                        ? 'featured'
                        : ''
                  }
                  onChange={e => {
                    if (e.target.value === 'keynote') {
                      handleFilterChange('isKeynoteSpeaker', true);
                      handleFilterChange('isFeaturedSpeaker', undefined);
                    } else if (e.target.value === 'featured') {
                      handleFilterChange('isFeaturedSpeaker', true);
                      handleFilterChange('isKeynoteSpeaker', undefined);
                    } else {
                      handleFilterChange('isKeynoteSpeaker', undefined);
                      handleFilterChange('isFeaturedSpeaker', undefined);
                    }
                  }}
                  label="Speaker Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="keynote">Keynote</MenuItem>
                  <MenuItem value="featured">Featured</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>

        {/* Table */}
        <DataTable<GuestSpeaker>
          columns={columns}
          data={speakers}
          actions={row => [
            {
              icon: <VisibilityIcon fontSize="small" />,
              onClick: () => handleViewSpeaker(row),
            },
            {
              icon: <EditIcon fontSize="small" />,
              onClick: () => handleEditSpeaker(row),
            },
            {
              icon: <MoreVertIcon fontSize="small" />,
              onClick: e => openActionMenu(e, row),
            },
          ]}
        />
        {/* <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: '#f9fafb' }}>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    color: '#6b7280',
                  }}
                >
                  Speaker
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    color: '#6b7280',
                  }}
                >
                  Organization
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    color: '#6b7280',
                  }}
                >
                  Contact
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    color: '#6b7280',
                  }}
                >
                  Type
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    color: '#6b7280',
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    color: '#6b7280',
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && speakers.length === 0 ? (
                <CircularProgress />
              ) : speakers.length === 0 && speakers.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Box>
                      <CalendarIcon
                        sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }}
                      />
                      <Typography sx={{ fontSize: '1rem', color: '#6b7280' }}>
                        No Speaker found
                      </Typography>
                      <Typography
                        sx={{ fontSize: '0.875rem', color: '#9ca3af', mt: 1 }}
                      >
                        Create your first Speaker to get started
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                speakers.map(speaker => {
                  const statusStyle = getStatusColor(speaker.isActive);

                  return (
                    <TableRow
                      key={speaker.id}
                      sx={{
                        '&:hover': { bgcolor: '#f9fafb' },
                        borderBottom: '1px solid #e5e7eb',
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                          }}
                        >
                          <Avatar
                            src={speaker.profileImageUrl}
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: '#7c3aed',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                            }}
                          >
                            {!speaker.profileImageUrl && (
                              <>
                                {speaker.firstName?.charAt(0)}
                                {speaker.lastName?.charAt(0)}
                              </>
                            )}
                          </Avatar>
                          <Box>
                            <Typography
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                color: '#111827',
                              }}
                            >
                              {speaker.title} {speaker.firstName}{' '}
                              {speaker.lastName}
                            </Typography>
                            <Typography
                              sx={{ fontSize: '0.75rem', color: '#6b7280' }}
                            >
                              {speaker.designation}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{ fontSize: '0.875rem', color: '#111827' }}
                        >
                          {speaker.organization || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography
                            sx={{ fontSize: '0.875rem', color: '#111827' }}
                          >
                            {speaker.email}
                          </Typography>
                          <Typography
                            sx={{ fontSize: '0.75rem', color: '#6b7280' }}
                          >
                            {speaker.phone || 'No phone'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                          }}
                        >
                          {speaker.isKeynoteSpeaker && (
                            <Chip
                              icon={<CampaignIcon sx={{ fontSize: 16 }} />}
                              label="Keynote"
                              size="small"
                              sx={{
                                bgcolor: '#fef3c7',
                                color: '#d97706',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                height: 24,
                              }}
                            />
                          )}
                          {speaker.isFeaturedSpeaker && (
                            <Chip
                              icon={<StarIcon sx={{ fontSize: 16 }} />}
                              label="Featured"
                              size="small"
                              sx={{
                                bgcolor: '#dbeafe',
                                color: '#1d4ed8',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                height: 24,
                              }}
                            />
                          )}
                          {!speaker.isKeynoteSpeaker &&
                            !speaker.isFeaturedSpeaker && (
                              <Typography
                                sx={{ fontSize: '0.75rem', color: '#9ca3af' }}
                              >
                                Regular
                              </Typography>
                            )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={speaker.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          sx={{
                            bgcolor: statusStyle.bg,
                            color: statusStyle.color,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            height: 24,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewSpeaker(speaker)}
                              sx={{
                                color: '#6b7280',
                                '&:hover': { color: '#3b82f6' },
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEditSpeaker(speaker)}
                              sx={{
                                color: '#6b7280',
                                '&:hover': { color: '#10b981' },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={e => openActionMenu(e, speaker)}
                            sx={{
                              color: '#6b7280',
                              '&:hover': { color: '#ef4444' },
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer> */}

        {/* Pagination */}
        {speakers.length > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              borderTop: '1px solid #e5e7eb',
              '& .MuiTablePagination-toolbar': {
                px: 3,
              },
            }}
          />
        )}
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={closeActionMenu}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
            border: '1px solid #e5e7eb',
            boxShadow:
              '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            mt: 1,
            minWidth: 180,
          },
        }}
      >
        <MenuItem
          onClick={() => selectedSpeaker && handleViewSpeaker(selectedSpeaker)}
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        {hasPermission(currentUser, Permissions.USER_UPDATE) && (
          <>
            <MenuItem
              onClick={() =>
                selectedSpeaker && handleEditSpeaker(selectedSpeaker)
              }
            >
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() =>
                selectedSpeaker && handleToggleStatus(selectedSpeaker)
              }
            >
              <ListItemIcon>
                {selectedSpeaker?.isActive ? (
                  <ToggleOffIcon fontSize="small" />
                ) : (
                  <ToggleOnIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText>
                {selectedSpeaker?.isActive ? 'Deactivate' : 'Activate'}
              </ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() =>
                selectedSpeaker && handleToggleFeatured(selectedSpeaker)
              }
            >
              <ListItemIcon>
                {selectedSpeaker?.isFeaturedSpeaker ? (
                  <StarBorderIcon fontSize="small" />
                ) : (
                  <StarIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText>
                {selectedSpeaker?.isFeaturedSpeaker
                  ? 'Remove Featured'
                  : 'Make Featured'}
              </ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() =>
                selectedSpeaker && handleToggleKeynote(selectedSpeaker)
              }
            >
              <ListItemIcon>
                <CampaignIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {selectedSpeaker?.isKeynoteSpeaker
                  ? 'Remove Keynote'
                  : 'Make Keynote'}
              </ListItemText>
            </MenuItem>
          </>
        )}
        {hasPermission(currentUser, Permissions.USER_DELETE) && (
          <MenuItem
            onClick={() =>
              selectedSpeaker && handleDeleteSpeaker(selectedSpeaker)
            }
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* View Dialog */}
      {viewDialogOpen && viewingSpeaker && (
        <SpeakerViewDialog
          open={viewDialogOpen}
          speaker={viewingSpeaker}
          onClose={() => {
            setViewDialogOpen(false);
            setViewingSpeaker(null);
          }}
        />
      )}

      {/* Edit Dialog */}
      {editDialogOpen && (
        <SpeakerEditDialog
          open={editDialogOpen}
          speaker={editingSpeaker}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingSpeaker(null);
          }}
          onSave={handleSpeakerSaved}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
              {' '}
              Confirm Delete{' '}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setDeleteDialogOpen(false)}
            disabled={loading}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete speaker "
            {speakerToDelete?.firstName} {speakerToDelete?.lastName}"? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              color: '#d32f2f',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.15)',
              },
            }}
          >
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GuestSpeakers;
